import { useEffect, useState } from 'react';
import { Palette, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useI18n } from '@/contexts/I18nContext';

export type Skin = 'basic' | 'kpop' | 'library' | 'cyber' | 'corporate' | 'precision';

interface SkinOption {
  id: Skin;
  emoji: string;
  swatch: string[];
}

const SKINS: SkinOption[] = [
  { id: 'basic', emoji: '✨', swatch: ['hsl(222 60% 22%)', 'hsl(45 93% 58%)', 'hsl(200 80% 45%)'] },
  { id: 'kpop', emoji: '🌸', swatch: ['hsl(322 90% 58%)', 'hsl(270 80% 65%)', 'hsl(190 90% 55%)'] },
  { id: 'library', emoji: '📚', swatch: ['hsl(42 28% 95%)', 'hsl(30 10% 10%)', 'hsl(32 45% 45%)'] },
  { id: 'corporate', emoji: '🏛️', swatch: ['hsl(215 50% 18%)', 'hsl(38 85% 50%)', 'hsl(210 25% 97%)'] },
  { id: 'cyber', emoji: '🎮', swatch: ['hsl(240 20% 6%)', 'hsl(152 100% 50%)', 'hsl(330 100% 55%)'] },
  { id: 'precision', emoji: '🎯', swatch: ['hsl(0 0% 6%)', 'hsl(0 0% 98%)', 'hsl(12 100% 50%)'] },
];

type SkinTr = { menuTitle: string; tooltip: string; items: Record<Skin, { label: string; desc: string }> };

const SKIN_I18N: Record<string, SkinTr> = {
  en: { menuTitle: '🎨 Choose design skin', tooltip: 'Change theme skin', items: {
    basic: { label: 'Basic', desc: 'Default navy + gold' },
    kpop: { label: 'K-Pop Vivid', desc: 'Gen Z hot pink gradient' },
    library: { label: 'Library Paper', desc: 'Minimal washi + brass' },
    corporate: { label: 'Corporate Trust', desc: 'Business navy + gold' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Black + neon green' },
    precision: { label: 'Precision Instrument', desc: 'Monochrome + laser red' },
  }},
  ko: { menuTitle: '🎨 디자인 스킨 선택', tooltip: '테마 스킨 변경', items: {
    basic: { label: '베이직', desc: '기본 네이비 + 골드' },
    kpop: { label: 'K-Pop 비비드', desc: 'Z세대 핫핑크 그라데이션' },
    library: { label: '도서관 페이퍼', desc: '미니멀 워시 + 브래스' },
    corporate: { label: '코퍼레이트 트러스트', desc: '비즈니스 네이비 + 골드' },
    cyber: { label: '사이버펑크 HUD', desc: '블랙 + 네온 그린' },
    precision: { label: '정밀 계측기', desc: '모노크롬 + 레이저 레드' },
  }},
  ja: { menuTitle: '🎨 デザインスキンを選択', tooltip: 'テーマスキン変更', items: {
    basic: { label: 'ベーシック', desc: 'ネイビー + ゴールド' },
    kpop: { label: 'K-Pop ビビッド', desc: 'Z世代ホットピンク' },
    library: { label: '図書館ペーパー', desc: 'ミニマル和紙 + 真鍮' },
    corporate: { label: 'コーポレート', desc: 'ビジネス紺 + 金' },
    cyber: { label: 'サイバーパンクHUD', desc: 'ブラック + ネオン緑' },
    precision: { label: '精密計測機器', desc: 'モノクローム + レッド' },
  }},
  'zh-CN': { menuTitle: '🎨 选择设计风格', tooltip: '更换主题风格', items: {
    basic: { label: '基本', desc: '深蓝 + 金色' },
    kpop: { label: 'K-Pop 鲜艳', desc: 'Z世代亮粉' },
    library: { label: '图书馆纸感', desc: '极简 + 黄铜' },
    corporate: { label: '商务信任', desc: '深蓝 + 金色' },
    cyber: { label: '赛博朋克 HUD', desc: '黑 + 霓虹绿' },
    precision: { label: '精密仪器', desc: '单色 + 红' },
  }},
  'zh-TW': { menuTitle: '🎨 選擇設計風格', tooltip: '更換主題風格', items: {
    basic: { label: '基本', desc: '深藍 + 金色' },
    kpop: { label: 'K-Pop 鮮豔', desc: 'Z世代亮粉' },
    library: { label: '圖書館紙感', desc: '極簡 + 黃銅' },
    corporate: { label: '商務信任', desc: '深藍 + 金色' },
    cyber: { label: '賽博龐克 HUD', desc: '黑 + 霓虹綠' },
    precision: { label: '精密儀器', desc: '單色 + 紅' },
  }},
  th: { menuTitle: '🎨 เลือกธีมดีไซน์', tooltip: 'เปลี่ยนธีม', items: {
    basic: { label: 'พื้นฐาน', desc: 'น้ำเงิน + ทอง' },
    kpop: { label: 'K-Pop สดใส', desc: 'ชมพูสด Gen Z' },
    library: { label: 'ห้องสมุด', desc: 'มินิมอล + ทองเหลือง' },
    corporate: { label: 'องค์กร', desc: 'ธุรกิจ น้ำเงิน + ทอง' },
    cyber: { label: 'ไซเบอร์พังก์', desc: 'ดำ + เขียวนีออน' },
    precision: { label: 'เครื่องมือแม่นยำ', desc: 'โมโนโครม + แดง' },
  }},
  vi: { menuTitle: '🎨 Chọn giao diện', tooltip: 'Đổi giao diện', items: {
    basic: { label: 'Cơ bản', desc: 'Xanh navy + vàng' },
    kpop: { label: 'K-Pop rực rỡ', desc: 'Hồng Gen Z' },
    library: { label: 'Thư viện', desc: 'Tối giản + đồng' },
    corporate: { label: 'Doanh nghiệp', desc: 'Navy + vàng' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Đen + xanh neon' },
    precision: { label: 'Thiết bị chính xác', desc: 'Đơn sắc + đỏ' },
  }},
  id: { menuTitle: '🎨 Pilih Skin Desain', tooltip: 'Ubah skin tema', items: {
    basic: { label: 'Basic', desc: 'Navy + emas' },
    kpop: { label: 'K-Pop Vivid', desc: 'Pink ngejreng Gen Z' },
    library: { label: 'Library', desc: 'Minimalis + brass' },
    corporate: { label: 'Corporate', desc: 'Bisnis navy + emas' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Hitam + hijau neon' },
    precision: { label: 'Precision', desc: 'Monokrom + merah' },
  }},
  ms: { menuTitle: '🎨 Pilih Skin Reka Bentuk', tooltip: 'Tukar skin tema', items: {
    basic: { label: 'Asas', desc: 'Navy + emas' },
    kpop: { label: 'K-Pop Vivid', desc: 'Pink terang Gen Z' },
    library: { label: 'Perpustakaan', desc: 'Minimalis + tembaga' },
    corporate: { label: 'Korporat', desc: 'Navy + emas' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Hitam + neon hijau' },
    precision: { label: 'Instrumen Tepat', desc: 'Monokrom + merah' },
  }},
  'pt-BR': { menuTitle: '🎨 Escolher tema', tooltip: 'Mudar tema', items: {
    basic: { label: 'Básico', desc: 'Azul-marinho + dourado' },
    kpop: { label: 'K-Pop Vibrante', desc: 'Rosa Gen Z' },
    library: { label: 'Biblioteca', desc: 'Minimalista + latão' },
    corporate: { label: 'Corporativo', desc: 'Marinho + dourado' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Preto + verde neon' },
    precision: { label: 'Instrumento de precisão', desc: 'Monocromo + vermelho' },
  }},
  my: { menuTitle: '🎨 ဒီဇိုင်းအသားရွေးပါ', tooltip: 'အသားပြောင်းရန်', items: {
    basic: { label: 'အခြေခံ', desc: 'အပြာရင့် + ရွှေ' },
    kpop: { label: 'K-Pop တောက်ပ', desc: 'Z-မျိုးဆက် ပန်းရောင်' },
    library: { label: 'စာကြည့်တိုက်', desc: 'ရိုးရှင်း + ကြေးနီ' },
    corporate: { label: 'ကော်ပိုရိတ်', desc: 'အပြာရင့် + ရွှေ' },
    cyber: { label: 'Cyberpunk HUD', desc: 'အနက် + နီယွန်အစိမ်း' },
    precision: { label: 'တိကျတဲ့ ကိရိယာ', desc: 'အနက်ဖြူ + အနီ' },
  }},
  de: { menuTitle: '🎨 Design-Skin wählen', tooltip: 'Theme-Skin ändern', items: {
    basic: { label: 'Basic', desc: 'Standard Navy + Gold' },
    kpop: { label: 'K-Pop Vivid', desc: 'Gen Z Hot Pink' },
    library: { label: 'Bibliothek', desc: 'Minimal + Messing' },
    corporate: { label: 'Corporate Trust', desc: 'Business Navy + Gold' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Schwarz + Neongrün' },
    precision: { label: 'Präzisionsgerät', desc: 'Monochrom + Laserrot' },
  }},
  es: { menuTitle: '🎨 Elegir tema visual', tooltip: 'Cambiar tema', items: {
    basic: { label: 'Básico', desc: 'Azul marino + oro' },
    kpop: { label: 'K-Pop Vívido', desc: 'Rosa Gen Z' },
    library: { label: 'Biblioteca', desc: 'Minimal + latón' },
    corporate: { label: 'Corporativo', desc: 'Marino + oro' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Negro + verde neón' },
    precision: { label: 'Instrumento de precisión', desc: 'Monocromo + rojo' },
  }},
  fr: { menuTitle: '🎨 Choisir un thème', tooltip: 'Changer de thème', items: {
    basic: { label: 'Basique', desc: 'Marine + or' },
    kpop: { label: 'K-Pop Vif', desc: 'Rose Gen Z' },
    library: { label: 'Bibliothèque', desc: 'Minimal + laiton' },
    corporate: { label: 'Corporate', desc: 'Marine + or' },
    cyber: { label: 'Cyberpunk HUD', desc: 'Noir + vert néon' },
    precision: { label: 'Instrument de précision', desc: 'Monochrome + rouge' },
  }},
  ar: { menuTitle: '🎨 اختر مظهر التصميم', tooltip: 'تغيير المظهر', items: {
    basic: { label: 'أساسي', desc: 'كحلي + ذهبي' },
    kpop: { label: 'K-Pop نابض', desc: 'وردي جيل Z' },
    library: { label: 'مكتبة', desc: 'بسيط + نحاسي' },
    corporate: { label: 'مؤسسي', desc: 'كحلي + ذهبي' },
    cyber: { label: 'سايبربانك HUD', desc: 'أسود + أخضر نيون' },
    precision: { label: 'أداة دقيقة', desc: 'أحادي + أحمر ليزر' },
  }},
};

const STORAGE_KEY = 'app-skin';

export function getStoredSkin(): Skin {
  if (typeof window === 'undefined') return 'basic';
  const v = localStorage.getItem(STORAGE_KEY) as Skin | null;
  return v && SKINS.some(s => s.id === v) ? v : 'basic';
}

export function applySkin(skin: Skin) {
  if (typeof document === 'undefined') return;
  if (skin === 'basic') {
    document.documentElement.removeAttribute('data-skin');
  } else {
    document.documentElement.setAttribute('data-skin', skin);
  }
}

const SkinSwitcher = () => {
  const { lang } = useI18n();
  const tr = SKIN_I18N[lang] ?? SKIN_I18N.en;
  const [skin, setSkin] = useState<Skin>('basic');

  useEffect(() => {
    const initial = getStoredSkin();
    setSkin(initial);
    applySkin(initial);
  }, []);

  const change = (next: Skin) => {
    setSkin(next);
    localStorage.setItem(STORAGE_KEY, next);
    applySkin(next);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          title={tr.tooltip}
          aria-label={tr.tooltip}
        >
          <Palette className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {tr.menuTitle}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SKINS.map(s => (
          <DropdownMenuItem
            key={s.id}
            onClick={() => change(s.id)}
            className="flex items-start gap-3 py-2.5 cursor-pointer"
          >
            <span className="text-lg leading-none mt-0.5">{s.emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{tr.items[s.id].label}</span>
                {skin === s.id && <Check className="w-4 h-4 text-accent" />}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{tr.items[s.id].desc}</div>
              <div className="flex gap-1 mt-1.5">
                {s.swatch.map((c, i) => (
                  <span
                    key={i}
                    className="inline-block w-4 h-4 rounded-full border border-border/50"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SkinSwitcher;
